import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useAuthUserContext } from "../../context/AuthUser";
import {
  OrderDetailFormValues,
  OrderDetailSaveButtonName,
} from "../../model/form/OrderDetailForm";
import { OrderStatus } from "../../util/Constant";

type Props = {
  /** 見積詳細から開かれたか */
  fromQuotationDetail?: boolean;
  readOnly: boolean;
  inpOrder: OrderDetailFormValues;
  /** 保存系ボタン押下 */
  onClickAction: (name: OrderDetailSaveButtonName) => void;
  /** 注文書PDF */
  onClickOutputPdf: () => void;
  /** 工事明細書PDF */
  onClickOutputCompQuotationPdf: () => void;
  onClose: () => void;
};

/**
 * 注文詳細モーダルのフッター
 * ステータス × ユーザ種別（顧客 / BEMAC）ごとに表示するボタンを切り替える。
 */
function OrderDetailFooter(props: Props) {
  const { fromQuotationDetail, readOnly, inpOrder, onClickAction, onClose } =
    props;
  const auth = useAuthUserContext();
  const { t: tc } = useTranslation("OrderDetail");

  const closeButton = (
    <Button className="b-btn-close" onClick={onClose} data-cy="Closeボタン">
      Close
    </Button>
  );

  if (fromQuotationDetail) {
    return (
      <>
        {(!readOnly || fromQuotationDetail) && (
          <Button
            className="b-btn-primary"
            onClick={(e) => onClickAction("発注")}
            data-cy="発注ボタン"
          >
            {tc("発注")}
          </Button>
        )}
        {closeButton}
      </>
    );
  }

  return (
    <>
      {
        // 下書き or 新規作成（顧客）
        inpOrder.statusClassValue === OrderStatus.DRAFT &&
          !auth.user().bemacFlg && (
            <>
              {!readOnly && (
                <>
                  {!inpOrder.isChangeOrder && (
                    <Button
                      className="b-btn-light"
                      onClick={(e) => onClickAction("一時保存")}
                      data-cy="一時保存ボタン"
                    >
                      {tc("一時保存")}
                    </Button>
                  )}
                  <Button
                    className="b-btn-primary"
                    onClick={(e) => onClickAction("発注")}
                    data-cy="発注ボタン"
                  >
                    {tc("発注")}
                  </Button>
                </>
              )}
            </>
          )
      }
      {
        // 注文中（顧客）
        inpOrder.statusClassValue === OrderStatus.ORDERED &&
          !auth.user().bemacFlg && (
            <>
              {!readOnly && (
                <Button
                  className="b-btn-primary"
                  onClick={(e) => onClickAction("注文取消依頼")}
                  data-cy="注文取消依頼ボタン"
                >
                  {tc("注文取消依頼")}
                </Button>
              )}
            </>
          )
      }
      {
        // 注文中（BEMAC）
        inpOrder.statusClassValue === OrderStatus.ORDERED &&
          auth.user().bemacFlg && (
            <>
              {!readOnly && (
                <>
                  <Button
                    className="b-btn-light"
                    onClick={(e) => onClickAction("注文中一時保存")}
                    data-cy="一時保存ボタン"
                  >
                    {tc("一時保存")}
                  </Button>
                  <Button
                    className="b-btn-light"
                    onClick={(e) => onClickAction("注文変更依頼")}
                    data-cy="注文変更依頼ボタン"
                  >
                    {tc("注文変更依頼")}
                  </Button>
                  <Button
                    className="b-btn-primary"
                    onClick={(e) => onClickAction("注文承認")}
                    data-cy="注文承認ボタン"
                  >
                    {tc("注文承認")}
                  </Button>
                </>
              )}
            </>
          )
      }
      {
        // 部品手配中(顧客)
        inpOrder.statusClassValue === OrderStatus.ARRANGING &&
          !auth.user().bemacFlg && (
            <>
              <Button
                className="b-btn-light"
                onClick={(e) => props.onClickOutputPdf()}
                data-cy="注文書PDFボタン"
              >
                {tc("注文書PDF")}
              </Button>
              {!readOnly && (
                <>
                  <Button
                    className="b-btn-primary"
                    onClick={(e) => onClickAction("注文取消依頼")}
                    data-cy="注文取消依頼ボタン"
                  >
                    {tc("注文取消依頼")}
                  </Button>
                  <Button
                    className="b-btn-light"
                    onClick={(e) => onClickAction("更新")}
                    data-cy="更新ボタン"
                  >
                    {tc("更新")}
                  </Button>
                </>
              )}
            </>
          )
      }
      {
        // 部品手配中(BEMAC)
        inpOrder.statusClassValue === OrderStatus.ARRANGING &&
          auth.user().bemacFlg && (
            <>
              {!auth.isEngineer() && (
                <Button
                  className="b-btn-light"
                  onClick={(e) => props.onClickOutputPdf()}
                  data-cy="注文書PDFボタン"
                >
                  {tc("注文書PDF")}
                </Button>
              )}
              {!readOnly && (
                <>
                  <Button
                    className="b-btn-light"
                    onClick={(e) => onClickAction("注文変更依頼")}
                    data-cy="注文変更依頼ボタン"
                  >
                    {tc("注文変更依頼")}
                  </Button>
                  <Button
                    className="b-btn-primary"
                    onClick={(e) => onClickAction("更新")}
                    data-cy="更新ボタン"
                  >
                    {tc("更新")}
                  </Button>
                </>
              )}
            </>
          )
      }
      {
        // 部品取消依頼中(BEMAC)
        inpOrder.statusClassValue === OrderStatus.REQUESTED_CANCEL &&
          auth.user().bemacFlg && (
            <>
              {!readOnly && (
                <Button
                  className="b-btn-primary"
                  onClick={(e) => onClickAction("注文取消承認")}
                  data-cy="注文取消承認ボタン"
                >
                  {tc("注文取消承認")}
                </Button>
              )}
            </>
          )
      }
      {
        // 注文否認(BEMAC)
        inpOrder.statusClassValue === OrderStatus.REQUESTED_CHANGE &&
          auth.user().bemacFlg && (
            <>
              {!readOnly && (
                <>
                  <Button
                    className="b-btn-light"
                    onClick={(e) => onClickAction("差戻")}
                    data-cy="差戻ボタン"
                  >
                    {tc("差戻")}
                  </Button>
                </>
              )}
            </>
          )
      }
      {
        // 注文否認(顧客)
        inpOrder.statusClassValue === OrderStatus.REQUESTED_CHANGE &&
          !auth.user().bemacFlg && (
            <>
              {!readOnly && (
                <>
                  {!inpOrder.replyDateTime && (
                    <Button
                      className="b-btn-light"
                      onClick={(e) => onClickAction("注文停止")}
                      data-cy="注文停止ボタン"
                    >
                      {tc("注文停止")}
                    </Button>
                  )}
                  <Button
                    className="b-btn-primary"
                    onClick={(e) => onClickAction("注文変更")}
                    data-cy="注文変更ボタン"
                  >
                    {tc("注文変更")}
                  </Button>
                </>
              )}
            </>
          )
      }
      {
        // 完了(BEMAC)
        inpOrder.statusClassValue === OrderStatus.COMPLETED &&
          auth.user().bemacFlg && (
            <>
              {!auth.isEngineer() && !inpOrder.dispatchFlg && (
                <Button
                  className="b-btn-light"
                  onClick={(e) => props.onClickOutputPdf()}
                  data-cy="注文書PDFボタン"
                >
                  {tc("注文書PDF")}
                </Button>
              )}
              {!readOnly && (
                <>
                  {inpOrder.dispatchFlg && (
                    <>
                      <Button
                        className="b-btn-light"
                        onClick={(e) => props.onClickOutputCompQuotationPdf()}
                        data-cy="工事明細書PDFボタン"
                      >
                        {tc("工事明細書PDF")}
                      </Button>
                      <Button
                        className="b-btn-light"
                        onClick={(e) => onClickAction("完工見積一時保存")}
                        data-cy="完工見積一時保存ボタン"
                      >
                        {tc("一時保存")}
                      </Button>
                      <Button
                        className="b-btn-light"
                        onClick={(e) => onClickAction("完工見積")}
                        data-cy="完工見積ボタン"
                      >
                        {tc("完工見積")}
                      </Button>
                    </>
                  )}
                  {!inpOrder.dispatchFlg && (
                    <Button
                      className="b-btn-light"
                      onClick={(e) => onClickAction("注文変更依頼")}
                      data-cy="注文変更依頼ボタン"
                    >
                      {tc("注文変更依頼")}
                    </Button>
                  )}
                  <Button
                    className="b-btn-primary"
                    onClick={(e) => onClickAction("出荷後キャンセル")}
                    data-cy="出荷後キャンセルボタン"
                  >
                    {tc("出荷後キャンセル")}
                  </Button>
                </>
              )}
            </>
          )
      }
      {
        // 完了(顧客)
        inpOrder.statusClassValue === OrderStatus.COMPLETED &&
          !auth.user().bemacFlg && (
            <>
              <Button
                className="b-btn-light"
                onClick={(e) => props.onClickOutputPdf()}
                data-cy="注文書PDFボタン"
              >
                {tc("注文書PDF")}
              </Button>
            </>
          )
      }
      {closeButton}
    </>
  );
}

export default OrderDetailFooter;
